import { type ChangeEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestFileTextProblem, RequestFileTextProblem } from '@quizdb/common';
import { Icon } from '../common/Icon';

/** `requestFileTextProblem`'s results, mapped to the message shown under the row (RQF-D1). */
const PROBLEM_KEYS: Record<RequestFileTextProblem, string> = {
  empty: 'error.RequestFileEmpty',
  notText: 'error.RequestFileNotText',
  tooLong: 'error.RequestFileTooLong',
};

export interface RequestFileFieldProps {
  value: { name: string; text: string } | undefined;
  onChange: (next: { name: string; text: string } | undefined) => void;
  /** The field's server or schema error, shown only while the file itself raises none. */
  error?: string;
  disabled?: boolean;
}

/**
 * New Job's optional request file (QZ-D141, RQF-D1): one .txt/.csv/.json/.md file read as UTF-8
 * text in the browser, nothing parsed. A pick that is empty, not valid UTF-8 (including a
 * renamed spreadsheet holding a NUL byte) or over the character limit is refused under the row,
 * and leaves an earlier value in place, rather than calling `onChange` (RQF-D3).
 */
export const RequestFileField = ({ value, onChange, error, disabled }: RequestFileFieldProps) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const numberFormat = new Intl.NumberFormat(i18n.language);

  const handlePick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again fires `onChange`; `file` is captured first.
    e.target.value = '';
    if (!file) {
      return;
    }

    const buffer = await file.arrayBuffer();
    let text: string;
    try {
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(new Uint8Array(buffer));
      // A leading BOM (U+FEFF) is dropped before the text is read any further (RQF-D1).
      text = decoded.startsWith('\uFEFF') ? decoded.slice(1) : decoded;
    } catch {
      setLocalError(t(PROBLEM_KEYS.notText));
      return;
    }

    const problem = requestFileTextProblem(text);
    if (problem) {
      setLocalError(t(PROBLEM_KEYS[problem]));
      return;
    }
    setLocalError(undefined);
    onChange({ name: file.name, text });
  };

  const handleRemove = () => {
    setLocalError(undefined);
    onChange(undefined);
  };

  const message = localError ?? error;

  return (
    <div>
      <label htmlFor="job-requestFile" className="form-label">
        {t('job.field.requestFile')}
      </label>
      <input
        ref={inputRef}
        type="file"
        id="job-requestFile"
        accept=".txt,.csv,.json,.md"
        className="d-none"
        aria-describedby={message ? 'job-requestFile-error' : undefined}
        disabled={disabled}
        onChange={handlePick}
      />

      {value ? (
        <div className="qz-file">
          <Icon name="file-lines" />
          <div className="flex-grow-1">
            <div className="fw-semibold">{value.name}</div>
            <div className="qz-small qz-muted">
              {t('job.new.requestFileChars', { chars: numberFormat.format(value.text.length) })}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {t('job.new.replace')}
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={disabled}
            onClick={handleRemove}
          >
            {t('job.new.requestFileRemove')}
          </button>
        </div>
      ) : (
        <>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {t('job.new.requestFileChoose')}
          </button>
          <div className="form-text">{t('job.new.requestFileHelp')}</div>
        </>
      )}

      {message && (
        <div id="job-requestFile-error" className="invalid-feedback d-block">
          {message}
        </div>
      )}
    </div>
  );
};

export default RequestFileField;
